/* eslint-disable @typescript-eslint/no-explicit-any */
import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Form, Row } from 'antd';
import { RuleObject } from 'antd/lib/form';
import { UploadChangeParam, UploadFile } from 'antd/lib/upload/interface';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';
import { ILeiContact, ILeiData, ILeiSubory, IZalozeniLeiInput } from 'types/leiTypes';

import AnchorComponent from 'components/atoms/AnchorComponent';
import ButtonComponent from 'components/atoms/ButtonComponent';
import DatePickerComponent from 'components/atoms/DatePickerComponent';
import InputComponent from 'components/atoms/InputComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import UploadComponent, { IFileList } from 'components/atoms/UploadComponent';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';

import fileDownload from 'services/fileDownload';
import checkOsobaOR from 'services/lei/checkOsobaOR';
import prepareFile from 'services/lei/fileDownload';

import { EAnoNe } from 'enums/anoNe';
import { ETypOsoby } from 'enums/lei/typOsoby';
import { ENotificationType } from 'enums/notificationType';
import { emailRegExp } from 'enums/regExp';
import { ETrueFalse } from 'enums/trueFalse';

import anoNe from 'data/codebooks/anoNeSelect';
import { leiTypOsobySelect } from 'data/codebooks/lei/leiTypOsobySelect';
import trueFalseSelect from 'data/codebooks/trueFalseSelect';

import dicValidation from 'utils/validations/dicValidation';
import emailValidation from 'utils/validations/emailValidation';
import phoneValidation from 'utils/validations/phoneValidation';

interface IOsoba {
    nextStep?: () => void;
    prevStep?: () => void;
    setFormData?: React.Dispatch<React.SetStateAction<ILeiData | undefined>>;
    formDataOsoba: ILeiData['osoba'];
    pruvodceData?: IZalozeniLeiInput;
    formDataSpolecnost?: ILeiData['spolecnost'];
    isEdit?: boolean;
    setIsEdit?: React.Dispatch<React.SetStateAction<boolean>>;
    setEditData?: React.Dispatch<React.SetStateAction<ILeiContact | undefined>>;
}

const Osoba: React.FC<IOsoba> = ({
    prevStep,
    nextStep,
    setFormData,
    setEditData,
    setIsEdit,
    formDataOsoba,
    pruvodceData,
    formDataSpolecnost,
    isEdit
}) => {
    const { lei, NotificationComponent, language } = useContext(Context);
    const [form] = Form.useForm();
    const [disableDownload, setDisableDownload] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [osobaCheckErr, setOsobaCheckErr] = useState<boolean>(false);
    const [fileErr, setFileErr] = useState<boolean>(false);
    const [prilozeneSoubory, setPrilozeneSoubory] = useState<ILeiSubory[]>(
        formDataOsoba && formDataOsoba.soubory ? formDataOsoba.soubory : []
    );
    const [fileList, setFileList] = useState<IFileList[] | undefined>(
        formDataOsoba?.soubory?.map((e, i) => {
            return {
                uid: String(i),
                name: e.orignazev ? e.orignazev : 'bez nazvu',
                fileName: e.tempnazev ? e.tempnazev : 'a',
                type: e.orignazev ? e.orignazev.split('.').slice(-1)[0] : '',
                size: 0,
                status: 'done'
            };
        })
    );
    const [uploading, setUploading] = useState<boolean>(false);
    const [showEmail2, setShowEmail2] = useState<boolean>(!!formDataOsoba?.contactemail2);
    const setForceRerender = useState<boolean>(false)[1];

    useEffect(() => {
        form.setFieldsValue({
            typosoby: formDataOsoba?.typosoby,
            madic:
                formDataOsoba && typeof formDataOsoba.madic === 'boolean'
                    ? formDataOsoba.madic
                        ? EAnoNe.ANO
                        : EAnoNe.NE
                    : undefined
        });
        setForceRerender(pv => !pv);
        formDataOsoba?.contactemail &&
            setTimeout(() => form.validateFields(['contactemail', 'fakturacniemail', 'contactphone']), 0); // rerender po nastaveni default values (inak sa niektore nezobrazia)

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onFinish = (fieldsValue: any, saveOnly?: ILeiSubory[]) => {
        const formDataSet = (entitycheck: boolean) => {
            const newData = {
                contactemail: fieldsValue.contactemail,
                ...(fieldsValue.contactemail2 && {
                    contactemail2: fieldsValue.contactemail2
                }),
                fakturacniemail: fieldsValue.fakturacniemail,
                contactjob: fieldsValue.contactjob,
                contactname: fieldsValue.contactname,
                contactphone: fieldsValue.contactphone,
                contactsurname: fieldsValue.contactsurname,
                typosoby: fieldsValue.typosoby,
                ...(fieldsValue.madic && {
                    madic: fieldsValue.madic === EAnoNe.ANO
                }),
                ...(fieldsValue.omezenaplatnost && {
                    omezenaplatnost: fieldsValue.omezenaplatnost === ETrueFalse.TRUE
                }),
                ...(fieldsValue.platnost && {
                    platnost: fieldsValue.platnost.format('YYYY-MM-DD')
                }),
                ...(fieldsValue.dic && {
                    dic: fieldsValue.dic
                }),
                ...((saveOnly || prilozeneSoubory).length
                    ? {
                          soubory: saveOnly || prilozeneSoubory
                      }
                    : {}),
                ...(entitycheck
                    ? {
                          entitycheck: 'OK'
                      }
                    : {})
            };
            setEditData
                ? setEditData(newData)
                : setFormData &&
                  setFormData(pv => {
                      return {
                          ...pv,
                          osoba: newData
                      };
                  });
        };
        if (!saveOnly) {
            if (
                (formDataSpolecnost?.adresy ? formDataSpolecnost.jurisdikce : pruvodceData?.jurisdikce) === 'CZ' &&
                !osobaCheckErr &&
                (!fileList || fileList?.length < 1)
            ) {
                setLoading(true);
                checkOsobaOR({
                    ic:
                        formDataSpolecnost?.registrationauthorityentityid ||
                        pruvodceData?.registrationauthorityentityid ||
                        '',
                    jmeno: fieldsValue.contactname,
                    prijmeni: fieldsValue.contactsurname
                })
                    .then(() => {
                        formDataSet(true);
                        nextStep && nextStep();
                    })
                    .catch(() => setOsobaCheckErr(true))
                    .finally(() => setLoading(false));
            } else {
                if (!fileList?.length) {
                    return setFileErr(true);
                }
                formDataSet(false);
                nextStep && nextStep();
            }
        } else if (saveOnly) {
            formDataSet(false);
        }
    };

    const uploadProps = {
        showDownloadIcon: true,
        ...(isEdit && {
            extramessage: disableDownload ? 'LEI_UPLOAD_FILLIN' : '',
            onButtonClick: () => {
                form.validateFields(
                    Object.keys(form.getFieldsValue()).filter((o: any) => !String(o).includes('soubor'))
                )
                    .then(() => setDisableDownload(false))
                    .catch(() => setDisableDownload(true));
            },
            beforeUpload: (file: UploadFile) => {
                // kontrola maximalnej velkosti s suboru
                if ((file.size || 0) / 1024 / 1024 > 20) {
                    NotificationComponent({
                        title: 'UPLOAD_MAX_SIZE',
                        description: 'UPLOAD_MAX_SIZE_TEXT',
                        type: ENotificationType.ERROR
                    });
                    return false;
                }
                if (disableDownload) return false;
                return true;
            }
        }),
        onChange: (data: UploadChangeParam) => {
            if (data.file.status === 'uploading' && setUploading) setUploading(true);
            if (data.file.status !== 'uploading' && setUploading) setUploading(false);
            if (data.fileList.length) {
                if (data.file.response) {
                    form.validateFields(['soubor']);
                    setFileErr(false);
                    const prilozeneSouboryNew = data.fileList
                        .filter(f => f.status && f.status !== 'error')
                        .map(e => {
                            return {
                                orignazev: e.name.normalize(),
                                tempnazev: e.response ? e.response.tempnazev : e.fileName
                            };
                        });

                    setPrilozeneSoubory(prilozeneSouboryNew);
                    // pri stranke zmenakontaktu nechceme ukladat po kazdom nahrati suboru
                    !setEditData && onFinish(form.getFieldsValue(), prilozeneSouboryNew);
                }
                setFileList(
                    data.fileList
                        .filter(f => f.status && f.status !== 'error')
                        .map(e => {
                            return {
                                uid: e.uid,
                                name: e.name.normalize(),
                                fileName: e.fileName ? e.fileName : e.response ? e.response.tempnazev : '',
                                type: e.type!,
                                size: e.size!,
                                status: e.status ? e.status : 'error'
                            };
                        })
                );
            }
        },
        onRemove: (file: UploadFile) => {
            if (fileList?.length) {
                const oldState = [...fileList];
                const newState = oldState.filter(e => e.uid !== file.uid);
                setFileList(newState);
                const newSoubory = [...prilozeneSoubory].filter(e => e.tempnazev !== file.fileName);
                setPrilozeneSoubory(newSoubory);
                // pri stranke zmenakontaktu nechceme ukladat po kazdom nahrati suboru
                !setEditData && onFinish(form.getFieldsValue(), newSoubory);
            }
        },
        onDownload: (file: UploadFile) =>
            prepareFile(file.fileName ? file.fileName : '', lei?.idVarsymZadosti?.varsym || '').then(resp => {
                fileDownload(resp.redirect, file.name ? file.name : 'file');
            }),
        fileList: fileList,
        buttonDisabled: fileList ? fileList.length > 4 : false
    };

    return (
        <>
            {language === 'en' && (
                <>
                    <Paragraph
                        text={'LEI_AKTUALIZACE_OPRAVNENI'}
                        style={{
                            display: 'block',
                            position: 'relative',
                            top: -6
                        }}
                    />
                    <p
                        style={{
                            display: 'block',
                            marginTop: 0,
                            marginBottom: 20
                        }}
                    >
                        <AnchorComponent
                            id="downloadPlnaMocBtn"
                            href={
                                window.location.href.includes('test')
                                    ? 'https://test.cdcp.cz/wp-content/uploads/md/LEI-plnaMocEN.pdf'
                                    : 'https://www.cdcp.cz/wp-content/uploads/md/LEI-plnaMocEN.pdf'
                            }
                            download="Lei power of attorney.pdf"
                            target="_blank"
                        >
                            <TranslateText>PLNA_MOC_VZOR</TranslateText>
                        </AnchorComponent>
                    </p>
                </>
            )}
            <LoadingWrapper loading={loading} loadingText="NACITANI_DAT">
                {!loading && (
                    <FormWrapper outForm={form} onFinish={onFinish} /* preserve={false}*/>
                        <SelectComponent
                            isFormItem={true}
                            name="typosoby"
                            defaultValue={formDataOsoba?.typosoby}
                            labelParagraph="JAKA_OSOBA"
                            data={leiTypOsobySelect}
                            onChange={() => setForceRerender(pv => !pv)}
                            placeholder={language === 'en' ? 'LEI_TYP_OSOBY_PLACEHOLDER' : undefined}
                        />
                        {form.getFieldValue('typosoby') === ETypOsoby.OPRAVNENA && language === 'cz' && (
                            <>
                                <Title text={'LEI_TYP_OSOBY_OPRAVNENA'} level={5} dividerTitle={true} />
                                {(formDataSpolecnost?.jurisdikce === 'SK' ||
                                    formDataSpolecnost?.jurisdikce === 'CZ') && (
                                    <Paragraph
                                        text={'LEI_AKTUALIZACE_OPRAVNENI'}
                                        style={{
                                            display: 'block',
                                            position: 'relative',
                                            top: -6
                                        }}
                                    />
                                )}
                                <Paragraph
                                    text={
                                        formDataSpolecnost?.jurisdikce === 'SK' ||
                                        formDataSpolecnost?.jurisdikce === 'CZ'
                                            ? 'LEI_AKTUALIZACE_OPRAVNENI_SUB'
                                            : 'LEI_AKTUALIZACE_OPRAVNENI_SUB_ZAHRANICNI'
                                    }
                                    style={{
                                        display: 'block',
                                        position: 'relative',
                                        top:
                                            formDataSpolecnost?.jurisdikce !== 'SK' &&
                                            formDataSpolecnost?.jurisdikce !== 'CZ'
                                                ? 2
                                                : 6
                                    }}
                                />
                                <p
                                    style={{
                                        display: 'block',
                                        marginTop: 2,
                                        marginBottom: 20
                                    }}
                                >
                                    <AnchorComponent
                                        id="downloadPlnaMocBtn"
                                        href={
                                            window.location.href.includes('test')
                                                ? 'https://test.cdcp.cz/wp-content/uploads/md/LEI-plnaMoc.pdf'
                                                : 'https://www.cdcp.cz/wp-content/uploads/md/LEI-plnaMoc.pdf'
                                        }
                                        download="Lei plná moc.pdf"
                                        target="_blank"
                                    >
                                        <TranslateText>PLNA_MOC_VZOR</TranslateText>
                                    </AnchorComponent>
                                </p>
                            </>
                        )}
                        {form.getFieldValue('typosoby') === ETypOsoby.POVERENA && language === 'cz' && (
                            <>
                                <Title text={'LEI_TYP_OSOBY_POVERENA'} level={5} dividerTitle={true} />
                                <Paragraph
                                    text={'LEI_AKTUALIZACE_OPRAVNENI2'}
                                    style={{
                                        display: 'block',
                                        position: 'relative',
                                        top: -6
                                    }}
                                />{' '}
                                <p
                                    style={{
                                        display: 'block',
                                        position: 'relative',
                                        top: -8,
                                        marginBottom: 20
                                    }}
                                >
                                    <AnchorComponent
                                        id="downloadPlnaMocBtn"
                                        href={
                                            window.location.href.includes('test')
                                                ? 'https://test.cdcp.cz/wp-content/uploads/md/LEI-plnaMoc.pdf'
                                                : 'https://www.cdcp.cz/wp-content/uploads/md/LEI-plnaMoc.pdf'
                                        }
                                        download="Lei plná moc.pdf"
                                        target="_blank"
                                    >
                                        <TranslateText>PLNA_MOC_VZOR</TranslateText>
                                    </AnchorComponent>
                                </p>
                            </>
                        )}
                        <InputComponent
                            label="JMENO"
                            name="contactname"
                            initialValue={formDataOsoba?.contactname}
                            maxLength={100}
                            onChange={() => osobaCheckErr && setOsobaCheckErr(false)}
                        />
                        <InputComponent
                            label="PRIJMENI"
                            name="contactsurname"
                            initialValue={formDataOsoba?.contactsurname}
                            maxLength={100}
                            onChange={() => osobaCheckErr && setOsobaCheckErr(false)}
                        />
                        <InputComponent
                            label="PRACOVNI_POZICE"
                            name="contactjob"
                            initialValue={formDataOsoba?.contactjob}
                            maxLength={500}
                        />
                        <InputComponent
                            label="TELEFON"
                            name="contactphone"
                            initialValue={formDataOsoba?.contactphone}
                            maxLength={17}
                            rules={phoneValidation()}
                        />
                        <InputComponent
                            label="EMAIL"
                            name="contactemail"
                            initialValue={formDataOsoba?.contactemail}
                            maxLength={128}
                            rules={emailValidation()}
                            onChange={() => setForceRerender(pv => !pv)}
                            labelExtra={
                                <Button
                                    id="c2PlusAltBtn"
                                    htmlType="button"
                                    type="primary"
                                    style={{
                                        display: 'inline-block',
                                        marginLeft: 20,
                                        width: 50
                                    }}
                                    icon={!showEmail2 ? <PlusOutlined /> : <MinusOutlined />}
                                    onClick={() => {
                                        setShowEmail2(pv => !pv);
                                    }}
                                />
                            }
                        />
                        {showEmail2 && (
                            <InputComponent
                                label="EMAIL2"
                                name="contactemail2"
                                initialValue={formDataOsoba?.contactemail2}
                                notRequired={true}
                                maxLength={128}
                                rules={[
                                    {
                                        required: false
                                    },
                                    () => ({
                                        validator(_rule: RuleObject, value: string) {
                                            if (value && (value.match(/@/g) || []).length > 1)
                                                return Promise.reject(
                                                    <TranslateText>NEVALIDNI_EMAIL_TWO</TranslateText>
                                                );
                                            if (!value || emailRegExp.test(value)) return Promise.resolve();
                                            return Promise.reject(<TranslateText>NEVALIDNI_EMAIL</TranslateText>);
                                        }
                                    })
                                ]}
                            />
                        )}
                        <InputComponent
                            label="EMAIL_FAKTURACANI"
                            name="fakturacniemail"
                            initialValue={formDataOsoba?.fakturacniemail}
                            maxLength={128}
                            rules={emailValidation()}
                        />
                        <SelectComponent
                            name="madic"
                            labelParagraph="ISIN_IDENTIFIKACE_PLATCE"
                            isFormItem={true}
                            data={anoNe}
                            onChange={() => {
                                form.resetFields(['dic']);
                                setForceRerender(pv => !pv);
                            }}
                            defaultValue={
                                formDataOsoba && typeof formDataOsoba.madic === 'boolean'
                                    ? formDataOsoba.madic
                                        ? EAnoNe.ANO
                                        : EAnoNe.NE
                                    : undefined
                            }
                        />
                        {form.getFieldValue('madic') === EAnoNe.ANO && (
                            <InputComponent
                                initialValue={formDataOsoba?.dic}
                                label="ISIN_IDENTIFIKACE_DIC"
                                name="dic"
                                maxLength={14}
                                rules={dicValidation()}
                            />
                        )}
                        <Form.Item
                            name="soubor"
                            label={<Paragraph text={'SOUBOR'} strong={true} />}
                            rules={[
                                {
                                    required:
                                        ((formDataSpolecnost?.adresy
                                            ? formDataSpolecnost.jurisdikce
                                            : pruvodceData?.jurisdikce) !== 'CZ' &&
                                            (formDataSpolecnost?.adresy
                                                ? formDataSpolecnost.jurisdikce
                                                : pruvodceData?.jurisdikce) !== 'SK') ||
                                        osobaCheckErr,
                                    validator() {
                                        try {
                                            if (osobaCheckErr && (!fileList || fileList.length < 1))
                                                return Promise.reject(
                                                    <TranslateText>NEVALIDNI_POVINNA_POLOZKA_UPLOAD</TranslateText>
                                                );
                                            return Promise.resolve();
                                        } catch {
                                            return Promise.reject();
                                        }
                                    }
                                }
                            ]}
                        >
                            <div
                                style={{
                                    marginBottom: 22
                                }}
                            >
                                {(formDataSpolecnost?.adresy
                                    ? formDataSpolecnost.jurisdikce
                                    : pruvodceData?.jurisdikce) !== 'CZ' &&
                                    (formDataSpolecnost?.adresy
                                        ? formDataSpolecnost.jurisdikce
                                        : pruvodceData?.jurisdikce) !== 'SK' && (
                                        <p>
                                            <TranslateText>LEI_VLOZTE_REJSTRIK</TranslateText>
                                        </p>
                                    )}
                                <UploadComponent
                                    {...uploadProps}
                                    accept={'.pdf, .rtf, .docx, .doc'}
                                    id="osobaUploadBtn"
                                />
                            </div>
                        </Form.Item>
                        {!!fileList?.length && (
                            <>
                                <SelectComponent
                                    name="omezenaplatnost"
                                    labelParagraph="LEI_PLATNOST_OMEZENA"
                                    isFormItem={true}
                                    data={trueFalseSelect}
                                    onChange={() => {
                                        setForceRerender(pv => !pv);
                                    }}
                                    defaultValue={
                                        formDataOsoba && typeof formDataOsoba.omezenaplatnost === 'boolean'
                                            ? String(formDataOsoba.omezenaplatnost)
                                            : undefined
                                    }
                                />
                                {form.getFieldValue('omezenaplatnost') === 'true' && (
                                    <DatePickerComponent
                                        label="LEI_DETAIL_PLATNOSTDOKUMENTU_DO"
                                        name="platnost"
                                        initialValue={
                                            formDataOsoba?.platnost ? dayjs(formDataOsoba.platnost) : undefined
                                        }
                                    />
                                )}
                            </>
                        )}

                        <Row className="button-row">
                            <ButtonComponent
                                onClick={() => (!isEdit ? prevStep?.() : setIsEdit?.(false))}
                                buttontext="ZPET"
                                id="osobaZpetBtn"
                                type="default"
                            />
                            <ButtonComponent
                                id="osobaPokracovatBtn"
                                style={{
                                    margin: '0 8px'
                                }}
                                type="primary"
                                onClick={() => form.submit()}
                                loading={uploading}
                                buttontext={'POKRACOVAT'}
                                extramessage={
                                    osobaCheckErr ? 'LEI_OSOBACHECK_ERROR' : fileErr ? 'SOUBOR_NOT_LOADED_OSOBA' : ''
                                }
                            />
                        </Row>
                    </FormWrapper>
                )}
            </LoadingWrapper>
        </>
    );
};

export default Osoba;
